import { LogoWrapper, TranscendLogo, useQueryParam } from '@main/core-ui';
import { TRANSCEND_HOMEPAGE_URI } from '@main/privacy-center/src/args';
import {
  selectDisabledPrivacyCenterAboutTranscend,
  selectRequiredPrivacyCenter,
  selectWhiteLabelPrivacyCenter,
} from '@main/privacy-center/src/containers/App/selectors';
import LocaleToggle from '@main/privacy-center/src/containers/LocaleToggle';
import {
  LOCALE_KEY,
  type LocaleValue,
  NATIVE_LANGUAGE_NAMES,
} from '@transcend-io/internationalization';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { heroPageMessages } from '../../routes/HeroPage/messages';
import { LogoImageWrapper } from '../Header/wrappers';
import { footerMessages } from './messages';
import {
  FooterItem,
  FooterNavLinkWrapper,
  FooterNavWrapper,
  FooterWrapper,
  TranscendWrapper,
} from './wrappers';

/**
 * The footer props
 */
export interface FooterProps {
  /** The URL of the company's home page */
  homepageUrl: string;
  /** The mail-to email for contact support inquiries */
  supportEmail?: string;
  /** The link to page detailing how Transcend works */
  aboutTranscendUri?: string;
  /** Whether it is on the About Transcend page or Privacy Center */
  isTranscendPath: boolean;
}

// Used for Unicode-aware sorting of locale labels
const collator = new Intl.Collator();

/**
 * The Page footer
 */
export function Footer({
  isTranscendPath,
  homepageUrl,
  supportEmail,
  aboutTranscendUri = '/about',
}: FooterProps): JSX.Element {
  const {
    locales,
    organization: { name: organizationName },
    assets: { logo },
  } = useSelector(selectRequiredPrivacyCenter);
  const { formatMessage } = useIntl();
  const localeOptions = useMemo(
    () =>
      ((locales as LocaleValue[]) || Object.values(LOCALE_KEY))
        .map((locale) => ({
          value: locale,
          // Get locale descriptor in locale's native language
          label: NATIVE_LANGUAGE_NAMES[locale],
        }))
        .sort((a, b) => collator.compare(a.label, b.label)),
    [locales],
  );
  const whiteLabelPrivacyCenter = useSelector(selectWhiteLabelPrivacyCenter);
  const disabledPrivacyCenterAboutTranscend = useSelector(
    selectDisabledPrivacyCenterAboutTranscend,
  );

  const { value: simple } = useQueryParam({ name: 'simple' });
  const useSimpleVersion = simple === 'true';

  const logoAlt = logo
    ? formatMessage(logo.alt || heroPageMessages.organizationLogoAlt, {
        organizationName,
      })
    : undefined;

  return (
    <FooterWrapper transcend={isTranscendPath.toString()}>
      {whiteLabelPrivacyCenter ? (
        <div />
      ) : disabledPrivacyCenterAboutTranscend ? (
        <TranscendLogo style={{ width: '112px' }} />
      ) : (
        <TranscendWrapper
          href={TRANSCEND_HOMEPAGE_URI.href}
          transcend={isTranscendPath.toString()}
          target="_blank"
          rel="noopener noreferer"
          aria-label={formatMessage(footerMessages.linkToTranscend)}
        >
          <TranscendLogo style={{ width: '112px' }} />
        </TranscendWrapper>
      )}

      {!useSimpleVersion ? (
        <FooterNavWrapper>
          {!isTranscendPath && !disabledPrivacyCenterAboutTranscend && (
            <FooterItem>
              <FooterNavLinkWrapper
                transcend={isTranscendPath.toString()}
                message={footerMessages.aboutTranscend}
                href={aboutTranscendUri}
                type="secondary"
              />
            </FooterItem>
          )}
          {supportEmail && (
            <FooterItem>
              <FooterNavLinkWrapper
                transcend={isTranscendPath.toString()}
                message={footerMessages.support}
                href={`mailto:${supportEmail}`}
              />
            </FooterItem>
          )}
          <FooterItem>
            <FooterNavLinkWrapper
              transcend={isTranscendPath.toString()}
              message={footerMessages.mainSite}
              href={homepageUrl}
            />
          </FooterItem>
          <FooterItem>
            {locales.length >= 2 && <LocaleToggle options={localeOptions} />}
          </FooterItem>
        </FooterNavWrapper>
      ) : logo ? (
        <FooterNavWrapper>
          <FooterItem>
            <LogoWrapper>
              <LogoImageWrapper
                src={logo.src}
                alt={logoAlt}
                className="footer-org-logo"
              />
            </LogoWrapper>
          </FooterItem>
          <FooterItem>
            {locales.length >= 2 && <LocaleToggle options={localeOptions} />}
          </FooterItem>
        </FooterNavWrapper>
      ) : (
        <FooterNavWrapper>
          <FooterItem>
            {locales.length >= 2 && <LocaleToggle options={localeOptions} />}
          </FooterItem>
        </FooterNavWrapper>
      )}
    </FooterWrapper>
  );
}

export default Footer;
